import { FC, useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { Button, Typography } from "theme";
import { Edit } from "theme/icons";

import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Dialog, DialogTitle } from "@mui/material";

import { useUpdateBookingPrice } from "../../../../api/hooks/useUpdateBookingPrice/useUpdateBookingPrice.ts";
import { InvertButton } from "../../../../theme/components/Button/Button.tsx";
import ControlledTextField from "../../../ControlledInputs/ControlledTextField.tsx";

import { PriceFormScheme, priceScheme } from "./validation.ts";

type EditPriceProps = {
  initialPrice: number;
}

const EditPrice: FC<EditPriceProps> = () => {
  const { id } = useParams();
  const [isEditPriceModalOpen, setIsEditPriceModalOpen] = useState(false);

  const updateBookingPriceProvider = useUpdateBookingPrice(id || "");

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<PriceFormScheme>({
    reValidateMode: "onSubmit",
    resolver: zodResolver(priceScheme),
    defaultValues: {
      newPrice: "0",
      changeReason: "",
    },
  });

  const onSubmit = useCallback(async (data: PriceFormScheme) => {
    try {
      await updateBookingPriceProvider.mutateAsync(data);
      reset();
      setIsEditPriceModalOpen(false);
    }catch (e) {
      console.error(e);
    }
  }, [updateBookingPriceProvider, reset]);

  return (
    <div>
      <Button onClick={() => setIsEditPriceModalOpen(true)} look="normal">
        <Edit />
      </Button>
      <Dialog sx={{
        "& .MuiPaper-root": {
          backgroundColor: "white",
          padding: "16px",
          minWidth: "300px",
        },
      }} open={isEditPriceModalOpen} onClose={() => setIsEditPriceModalOpen(false)}>
        <DialogTitle>Додати послугу</DialogTitle>

        <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate my={3.5}>
          <Box mb={2}>
            <ControlledTextField
              control={control}
              required
              name="newPrice"
              label="Додати до ціни"
              placeholder="Введіть суму для додавання"
              type="number"
            />
            {errors.newPrice?.message &&
              <Typography color="error" variant="body2">{errors.newPrice.message}</Typography>}
          </Box>

          <Box mb={2}>
            <ControlledTextField
              control={control}
              required
              name="changeReason"
              label="Послуга"
              placeholder="Введіть назву послуги"
              type="text"
            />
            {errors.changeReason?.message &&
              <Typography color="error" variant="body2">{errors.changeReason.message}</Typography>}
          </Box>

          <InvertButton fullWidth type="submit" disabled={isSubmitting} loading={isSubmitting}>
            Додати послугу
          </InvertButton>
        </Box>
      </Dialog>
    </div>
  );
};

export default EditPrice;
